import { Injectable } from '@angular/core';
import { PrivateBrowsingDetectorService } from './private-browsing-detector.service';

@Injectable({
  providedIn: 'root'
})
export class IndexedDBRecoveryService {
  private readonly CORRUPTION_ERROR_KEYWORDS = [
    'refusing to open IndexedDB',
    'potential corruption',
    'IndexedDB database',
    'clear site data',
    'a newer version of the firestore sdk',
    'connection to indexed database server lost',
  ];

  private recoveryInProgress = false;
  private globalListenersRegistered = false;

  // Keywords that indicate actual IndexedDB backing store errors (not settings errors)
  private readonly BACKING_STORE_ERROR_KEYWORDS = [
    'backing store',
    'internal error'
  ];

  // Keywords that indicate Firebase settings configuration errors (NOT corruption)
  private readonly SETTINGS_ERROR_KEYWORDS = [
    'settings can no longer be changed',
    'Firestore has already been started'
  ];

  private readonly PRIVATE_BROWSING_ERROR_KEYWORDS = [
    'undefined is not an object',
    'createObjectStore',
    'incompatible browser',
    'indexeddb is not available',
    'private browsing',
    'null is not an object'
  ];

  constructor(private privateBrowsingDetector: PrivateBrowsingDetectorService) { }

  /**
   * Registers window.onerror and unhandledrejection listeners to catch IndexedDB
   * corruption errors that fire outside Angular's zone (e.g. onupgradeneeded).
   */
  registerGlobalErrorListeners(): void {
    if (this.globalListenersRegistered || typeof window === 'undefined') {
      return;
    }
    this.globalListenersRegistered = true;

    const handlePotentialCorruption = (error: unknown, fallbackMessage?: unknown): boolean => {
      const normalized = error instanceof Error
        ? error
        : new Error(String(error ?? fallbackMessage ?? 'Unknown error'));
      if (!this.isIndexedDBCorruptionError(normalized)) {
        return false;
      }
      this.handleCorruptionAndReload()
      return true;
    };

    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (handlePotentialCorruption(error, message)) {
        return true;
      }

      if (typeof originalOnError === 'function') {
        return originalOnError.call(window, message, source, lineno, colno, error);
      }

      return false;
    };

    window.addEventListener('unhandledrejection', (event) => {
      if (handlePotentialCorruption(event.reason)) {
        event.preventDefault();
      }
    });
  }

  /**
   * Checks if an error is related to IndexedDB corruption
   */
 isIndexedDBCorruptionError(error: Error | any): boolean {
  try {
    if (!error) return false;

    const errorMessage = error?.message || error?.toString?.() || '';
    const errorMessageLower = errorMessage.toLowerCase();

    // Prevent crashes from invalid values (objects without toString, nulls, etc.)
    if (typeof errorMessageLower !== 'string') {
      return false;
    }

    // 1. Settings-related errors (NOT corruption)
    const isSettingsError = this.SETTINGS_ERROR_KEYWORDS?.some(keyword =>
      errorMessageLower.includes(keyword.toLowerCase())
    );

    if (isSettingsError) {
      console.warn(
        '[IndexedDB] Detected Firebase settings error (not corruption):',
        errorMessage
      );
      return false;
    }

    // 2. Corruption keywords
    const hasCorruptionKeyword = this.CORRUPTION_ERROR_KEYWORDS?.some(keyword =>
      errorMessageLower.includes(keyword.toLowerCase())
    );

    if (hasCorruptionKeyword) {
      return true;
    }

    // 3. Backing store errors
    const hasBackingStoreKeyword = this.BACKING_STORE_ERROR_KEYWORDS?.some(keyword =>
      errorMessageLower.includes(keyword.toLowerCase())
    );

    if (hasBackingStoreKeyword) {
      const errorName = (error?.name || '').toLowerCase();

      const isIndexedDBError =
        errorName.includes('idb') ||
        errorName.includes('indexeddb') ||
        errorMessageLower.includes('indexeddb.open');

      return isIndexedDBError;
    }

    return false;
  } catch (e) {
    console.error(
      '[IndexedDB] Error while checking corruption error type:',
      e
    );
    return false; // fail-safe
  }
}



  /**
   * Checks if an error is related to private browsing mode
   * This includes Firebase Installations SDK errors when IndexedDB is restricted
   */
  isPrivateBrowsingError(error: Error): boolean {
    if (!error) return false;

    const errorMessage = (error.message || error.toString()).toLowerCase();
    const errorStack = (error.stack || '').toLowerCase();

    // Check for private browsing specific errors
    const hasPrivateBrowsingKeyword = this.PRIVATE_BROWSING_ERROR_KEYWORDS.some(keyword =>
      errorMessage.includes(keyword.toLowerCase()) || errorStack.includes(keyword.toLowerCase())
    );

    // Check for Firebase Installations specific error pattern
    const isFirebaseInstallationsError =
      errorStack.includes('@firebase/installations') ||
      errorStack.includes('dbpromise.upgrade') ||
      errorMessage.includes('createobjectstore');

    return hasPrivateBrowsingKeyword || isFirebaseInstallationsError;
  }

  /**
   * Checks if an error is the specific iOS Safari createObjectStore race condition
   * This is the error: "undefined is not an object (evaluating 'f1.createObjectStore')"
   */
  isCreateObjectStoreRaceCondition(error: Error): boolean {
    if (!error) return false;

    const errorMessage = (error.message || error.toString()).toLowerCase();
    const errorStack = (error.stack || '').toLowerCase();

    // Check for the specific pattern from the Sentry issue
    const hasRaceConditionPattern = 
      (errorMessage.includes('undefined is not an object') || errorMessage.includes('null is not an object')) &&
      (errorMessage.includes('createobjectstore') || errorStack.includes('createobjectstore'));

    // Check if it's from Firebase Installations SDK (common location for this error)
    const isFromFirebaseInstallations = 
      errorStack.includes('@firebase/installations') ||
      errorStack.includes('firebase-installations-database') ||
      errorStack.includes('vendor.') || // Minified vendor bundle
      errorStack.includes('upgrade') ||
      errorStack.includes('onupgradeneeded');

    return hasRaceConditionPattern && isFromFirebaseInstallations;
  }

  /**
   * Checks if an error is related to IndexedDB unavailability (corruption OR private browsing)
   */
  isIndexedDBUnavailableError(error: Error): boolean {
    return this.isIndexedDBCorruptionError(error) || this.isPrivateBrowsingError(error);
  }

  /**
   * Clears all IndexedDB databases and related storage to recover from corruption
   */
  async clearCorruptedIndexedDB(): Promise<void> {


    try {
      // Clear Firebase-specific data from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('firebase:') ||
          key.startsWith('firebaseui::') ||
          key.includes('firestore')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Get all IndexedDB databases
      if (window.indexedDB && typeof window.indexedDB.databases === 'function') {
        const databases = await window.indexedDB.databases();

        // Delete Firebase/Firestore related databases
        const deletePromises = databases
          .filter(db => {
            const dbName = db.name || '';
            return dbName.includes('firebaseLocalStorage') ||
              dbName.includes('firestore') ||
              dbName.includes('firebase');
          })
          .map(db => {
            return new Promise<void>((resolve, reject) => {
              const deleteRequest = window.indexedDB.deleteDatabase(db.name!);
              deleteRequest.onsuccess = () => {
                console.log(`Successfully deleted database: ${db.name}`);
                resolve();
              };
              deleteRequest.onerror = () => {
                console.warn(`Failed to delete database: ${db.name}`);
                resolve(); // Resolve anyway to not block the recovery process
              };
              deleteRequest.onblocked = () => {
                console.warn(`Deletion of database ${db.name} was blocked`);
                resolve(); // Resolve anyway to not block the recovery process
              };
            });
          });

        await Promise.all(deletePromises);
      }

    } catch (error) {
      console.error('Error during IndexedDB cleanup:', error);
      // Continue with reload even if cleanup fails
    }
  }

  /**
   * Handles IndexedDB corruption by clearing databases and reloading the page
   */
  async handleCorruptionAndReload(): Promise<void> {
    if (this.recoveryInProgress) {
      return;
    }
    this.recoveryInProgress = true;

    try {
      // First check if this is private browsing mode
      const isPrivate = await this.privateBrowsingDetector.detectPrivateBrowsing();
      if (isPrivate) {
        return;
      }

      const recoveryAttempts = parseInt(sessionStorage.getItem('indexeddb_recovery_attempts') || '0', 10);

      if (recoveryAttempts >= 3) {
      // Allow the application to continue without persistence
        sessionStorage.removeItem('indexeddb_recovery_attempts');
        return;
      }

      sessionStorage.setItem('indexeddb_recovery_attempts', (recoveryAttempts + 1).toString());

      await this.clearCorruptedIndexedDB();

      window.location.reload();
    } finally {
      this.recoveryInProgress = false;
    }
  }

  /**
   * Resets the recovery attempt counter (call this on successful initialization)
   */
  resetRecoveryAttempts(): void {
    sessionStorage.removeItem('indexeddb_recovery_attempts');
  }

  /**
   * Wraps a Firestore operation with IndexedDB corruption error handling
   */
  async wrapFirestoreOperation<T>(
    operation: () => Promise<T>,
    fallbackValue?: T
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (this.isIndexedDBCorruptionError(error)) {
        await this.handleCorruptionAndReload();

        // If we reach here (shouldn't happen due to reload), return fallback
        if (fallbackValue !== undefined) {
          return fallbackValue;
        }
        throw error;
      }
      // Re-throw non-corruption errors
      throw error;
    }
  }
}